/**
 * Lectura y escritura de la maquinaria (`company_equipment`, F2 26-09-2026).
 *
 * Sin react-query a propósito: la usan también el registro manual y crear
 * tarea, que cargan sus listas al abrirse con consultas sueltas (y sus pruebas
 * de render no montan un QueryClient). La pantalla de Equipo la envuelve en su
 * propio `useQuery` (`useCompanyEquipment`).
 *
 * La RLS manda: la lee toda la empresa y la escribe solo el admin (D6, E6). Si
 * alguien sin permiso llega a guardar, el error dice POR QUÉ (E8).
 */
import { supabase } from "@/integrations/supabase/client";
import type { EquipmentRow, EquipmentType } from "@/lib/machinery/equipmentRules";
import { equipmentRegime } from "@/lib/machinery/equipmentRules";

/* El cliente con el que se escribe: el del navegador por defecto; la tarjeta del
   asistente (CU7) pasa el suyo, que en las pruebas es uno de mentira. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Db = any;

export interface CompanyEquipment extends EquipmentRow {
  company_id: string;
  created_at?: string;
}

const COLUMNS =
  "id, company_id, location_id, equipment_type, description, brand, model, roma_number, census_reference, " +
  "acquired_on, last_inspection_on, is_default, active, archived_at, created_at";

/** La clave de react-query de la maquinaria de una empresa (la invalida también la tarjeta del asistente). */
export const companyEquipmentQueryKey = (companyId: string | undefined) => ["company-equipment", companyId] as const;

/** La maquinaria de la empresa, sin la archivada (salvo que se pida). */
export async function fetchCompanyEquipment(
  companyId: string,
  opts: { includeArchived?: boolean } = {},
  db: Db = supabase,
): Promise<CompanyEquipment[]> {
  let q = db.from("company_equipment").select(COLUMNS).eq("company_id", companyId);
  if (!opts.includeArchived) q = q.is("archived_at", null);
  const { data, error } = await q.order("description", { ascending: true });
  if (error) throw error;
  return (data ?? []) as unknown as CompanyEquipment[];
}

export interface EquipmentDraft {
  equipment_type: EquipmentType;
  description: string;
  brand: string;
  model: string;
  roma_number: string;
  census_reference: string;
  acquired_on: string;
  last_inspection_on: string;
  location_id: string | null;
  is_default: boolean;
}

const orNull = (s: string) => {
  const v = s.trim();
  return v ? v : null;
};

/** Alta o edición. Devuelve la fila guardada. */
export async function saveEquipment(
  companyId: string,
  userId: string | null,
  draft: EquipmentDraft,
  id?: string | null,
  db: Db = supabase,
): Promise<CompanyEquipment> {
  const row = {
    company_id: companyId,
    equipment_type: draft.equipment_type,
    description: draft.description.trim(),
    brand: orNull(draft.brand),
    model: orNull(draft.model),
    roma_number: orNull(draft.roma_number),
    census_reference: orNull(draft.census_reference),
    acquired_on: orNull(draft.acquired_on),
    last_inspection_on: orNull(draft.last_inspection_on),
    location_id: draft.location_id,
    is_default: draft.is_default,
  };
  const res = id
    ? await db.from("company_equipment").update(row).eq("id", id).select(COLUMNS).single()
    : await db
        .from("company_equipment")
        .insert({ ...row, created_by: userId })
        .select(COLUMNS)
        .single();
  if (res.error) throw res.error;
  return res.data as unknown as CompanyEquipment;
}

/**
 * El nº de inscripción de UNA máquina que ya existe, en su columna según el tipo:
 * el de censo autonómico si va al censo (fertirrigación, instalación fija), el
 * ROMA si no. Lo usa el asistente del cuaderno (CU7) para «la cuba 1 tiene ROMA
 * …» sin reescribir el resto de la ficha (`saveEquipment` escribe la fila
 * entera). La mochila va exenta y no lleva nº: `exenta`. Una máquina que ya no
 * está (o archivada, o de otra empresa): `no_existe`. 0 filas escritas = la RLS
 * (solo el admin): `permiso`.
 */
export async function setEquipmentNumber(
  companyId: string,
  equipmentId: string,
  numero: string,
  db: Db = supabase,
): Promise<"ok" | "no_existe" | "exenta" | "permiso"> {
  const { data, error } = await db
    .from("company_equipment")
    .select("id, equipment_type, archived_at")
    .eq("company_id", companyId)
    .eq("id", equipmentId)
    .limit(1);
  if (error) throw error;
  const fila = Array.isArray(data) ? (data[0] as { equipment_type?: string; archived_at?: string | null } | undefined) : undefined;
  if (!fila || fila.archived_at) return "no_existe";
  const regimen = equipmentRegime(fila.equipment_type);
  if (regimen === "exenta") return "exenta";
  const cambios = regimen === "censo" ? { census_reference: numero } : { roma_number: numero };
  const res = await db.from("company_equipment").update(cambios).eq("id", equipmentId).eq("company_id", companyId).select("id");
  if (res.error) throw res.error;
  return Array.isArray(res.data) && res.data.length === 1 ? "ok" : "permiso";
}

/**
 * Archivar, no borrar: los partes ya hechos guardan su copia, pero el 1.3 del
 * cuaderno de un año pasado la sigue necesitando. Deja de ser la de por defecto
 * (lo hace el trigger de la tabla).
 */
export async function archiveEquipment(id: string): Promise<void> {
  const { error } = await supabase
    .from("company_equipment")
    .update({ archived_at: new Date().toISOString(), active: false })
    .eq("id", id);
  if (error) throw error;
}

/**
 * Clave i18n del motivo de un fallo al guardar (E8: los fallos dicen por qué).
 * 42501 = la RLS lo rechaza (no es admin); el trigger avisa si la finca es de
 * otra empresa.
 */
export function equipmentErrorKey(error: unknown): { key: string; code: string } {
  const e = error as { code?: string; message?: string } | null;
  const code = e?.code ?? "";
  const message = e?.message ?? "";
  if (code === "42501" || /row-level security/i.test(message)) {
    return { key: "applicationContext.machinery.errors.onlyAdmin", code };
  }
  if (/EQUIPMENT_LOCATION_NOT_IN_COMPANY/.test(message)) {
    return { key: "applicationContext.machinery.errors.locationOtherCompany", code };
  }
  if (code === "23514") return { key: "applicationContext.machinery.errors.descriptionRequired", code };
  return { key: "applicationContext.machinery.errors.generic", code: code || "?" };
}
